// src/pages/Pursuit/ManagerAssessmentRecommendationsByUser.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Box, Heading, Text, Button, Spinner, Layer, Notification, Anchor } from "grommet";
import { useParams } from "react-router-dom";
import axios from "axios";
import { HeaderNavigation } from "../../components/Headers/PursuitHeader";
import { StickyFooter } from "../../components/Footers/GlobalFooter";
import { ProductsAssessment } from "../../components/PursuitAccess/ProductsAssessment";
import { DigitalLearningPath } from "../../components/QuizResultsComponents/DigitalLearningPath";
import { LearningPathModal } from "../../components/PursuitAccess/LearningPathModal";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("en-GB") : "—");
const fmtDateTime = (d, t) => {
    const date = d ? new Date(d).toLocaleDateString("en-GB") : "";
    return [date, t || ""].filter(Boolean).join(" ");
};

/** Mini block shown inside the modal */
const AssessmentBlock = ({ user_id, assessment_id, compact = false }) => {
    const [showLP, setShowLP] = useState(false);
    const [skillId, setSkillId] = useState(null);

    return (
        <Box
            background="white"
            pad={compact ? "small" : "medium"}
            round="small"
            elevation="small"
            fill="horizontal"
            style={{ overflow: "visible", position: "relative", minWidth: 0 }}
            gap="small"
        >
            <ProductsAssessment
                user_id={user_id}
                assessment_id={assessment_id}
                onShowLearningPath={(id) => {
                    setSkillId(id);
                    setShowLP(true);
                }}
            />
            {showLP && (
                <Box
                    margin={{ top: "small" }}
                    background="light-2"
                    pad="small"
                    round="small"
                    style={{ overflow: "visible" }}
                >
                    <LearningPathModal
                        user_id={user_id}
                        skills_id={skillId}
                        assessment_id={assessment_id}
                        onClose={() => setShowLP(false)}
                    />
                </Box>
            )}
            <DigitalLearningPath user_id={user_id} assessment_id={assessment_id} />
        </Box>
    );
};

// --- helpers for courses ---
const parseCoursesResponse = (data) => {
    const list = Array.isArray(data)
        ? data
        : Array.isArray(data?.courses)
            ? data.courses
            : Array.isArray(data?.data?.courses)
                ? data.data.courses
                : Array.isArray(data?.data)
                    ? data.data
                    : [];
    return list.map((c) => ({
        id: c.id || c.course_id || "",
        title: c.title || c.course_title || c.name || "",
        provider: c.provider || c.platform || c.source || "",
        url: c.url || c.link || c.course_url || "",
    }));
};

const getCoursesForUser = async (userId, assessmentId) => {
    try {
        const { data } = await axios.get(
            `/api/dashboard/getAllCoursesByUserAssessment/${userId}/${assessmentId}`
        );
        return parseCoursesResponse(data);
    } catch (e) {
        console.warn("[Courses] fetch failed for user:", userId, e);
        return [];
    }
};

const ManagerAssessmentRecommendationsPage = () => {
    const { assessment_id } = useParams();

    const [loading, setLoading] = useState(true);
    const [group, setGroup] = useState(null); // { assessment_id, assessment_meta, entries: [...] }
    const [error, setError] = useState(null);
    const [coursesLoading, setCoursesLoading] = useState(false);
    const [coursesUser, setCoursesUser] = useState(null);

    // Modal state for user recommendations
    const [recUser, setRecUser] = useState(null);

    // Toast
    const [toast, setToast] = useState("");

    // Courses cache per user_id for this assessment
    const [coursesByUser, setCoursesByUser] = useState({});

    // Fetch grouped assessments and pick this one
    useEffect(() => {
        let live = true;
        (async () => {
            setLoading(true);
            setError(null);
            try {
                const { data } = await axios.get("/api/pursuit/get-manager-assessments");
                const groups = Array.isArray(data.assessments) ? data.assessments : [];
                const match = groups.find((g) => String(g.assessment_id) === String(assessment_id));
                if (live) {
                    setGroup(match || null);
                    if (!match) setError("Assessment not found or has no entries.");
                }
            } catch {
                if (live) setError("Unable to load assessment recommendations.");
            } finally {
                if (live) setLoading(false);
            }
        })();
        return () => {
            live = false;
        };
    }, [assessment_id]);

    // Choose one “best” row per user (prefer completed, then latest end_date/id)
    const users = useMemo(() => {
        if (!group?.entries?.length) return [];
        const byUser = new Map();
        for (const e of group.entries) {
            const key = String(e.user_id);
            const prev = byUser.get(key);
            const score = (x) => [
                x?.is_complete === 1 ? 1 : 0,
                x?.end_date ? new Date(x.end_date).getTime() : 0,
                x?.id ?? 0,
            ];
            if (!prev || score(e) > score(prev)) byUser.set(key, e);
        }
        return Array.from(byUser.values()).sort((a, b) => {
            const da = a.end_date ? new Date(a.end_date).getTime() : 0;
            const db = b.end_date ? new Date(b.end_date).getTime() : 0;
            return db - da;
        });
    }, [group]);

    // Header summary/meta (plus unique skills for this assessment)
    const headerMeta = useMemo(() => {
        if (!group) return null;
        const first = group.entries?.[0] || {};
        const usersCount = users.length;
        const completed = users.filter((u) => u.is_complete === 1).length;

        const uniqueSkills = new Set();
        (group.entries || []).forEach((e) => {
            [e.topic1_name, e.topic2_name, e.topic3_name, e.topic4_name, e.topic5_name]
                .filter(Boolean)
                .forEach((name) => uniqueSkills.add(name));
        });

        return {
            name: group.assessment_meta?.assessment_name || "Assessment",
            createdDate: group.assessment_meta?.creation_date || null,
            createdTime: group.assessment_meta?.creation_time || null,
            managerName: first.manager_name || "N/A",
            managerEmail: first.manager_email || "",
            managerCountry: first.manager_country || "N/A",
            usersCount,
            completed,
            skills: Array.from(uniqueSkills),
        };
    }, [group, users]);

    // Fetch recommended courses for all COMPLETED users (for showing in the cards)
    useEffect(() => {
        let cancelled = false;
        (async () => {
            if (!group?.assessment_id || users.length === 0) {
                setCoursesByUser({});
                return;
            }
            const completed = users.filter((u) => u.is_complete === 1);
            if (completed.length === 0) {
                setCoursesByUser({});
                return;
            }
            const settled = await Promise.allSettled(
                completed.map((u) => getCoursesForUser(u.user_id, group.assessment_id))
            );
            const map = {};
            settled.forEach((res, idx) => {
                const u = completed[idx];
                map[u.user_id] = res.status === "fulfilled" ? res.value : [];
            });
            if (!cancelled) setCoursesByUser(map);
        })();
        return () => {
            cancelled = true;
        };
    }, [group?.assessment_id, users]);

    const openCoursesModal = async (u) => {
        setCoursesUser(u);
        if (!coursesByUser[u.user_id]) {
            setCoursesLoading(true);
            const list = await getCoursesForUser(u.user_id, group.assessment_id);
            setCoursesByUser((prev) => ({ ...prev, [u.user_id]: list }));
            setCoursesLoading(false);
        }
    };


    // ---------- Export (all users in this assessment, with courses) ----------
    const handleExport = async () => {
        if (!group || users.length === 0) return;

        const settled = await Promise.allSettled(
            users.map((u) => getCoursesForUser(u.user_id, group.assessment_id))
        );
        const coursesByIdx = settled.map((s) => (s.status === "fulfilled" ? s.value : []));

        const rows = users.map((u, idx) => {
            const courses = coursesByIdx[idx] || [];
            const courseTitles = courses.map((c) => c.title).filter(Boolean).join("; ");

            return {
                "Assessment Name": headerMeta?.name || "",
                Created: fmtDateTime(headerMeta?.createdDate, headerMeta?.createdTime),
                "Manager Name": headerMeta?.managerName || "",
                "Manager Email": headerMeta?.managerEmail || "",
                "Manager Country": headerMeta?.managerCountry || "",
                "User Name": u.user_name || "",
                "User Email": u.user_email || "",
                "User Country": u.user_country || "",
                Completed: u.is_complete === 1 ? "Yes" : "No",
                Start: fmtDateTime(u.start_date, u.start_time),
                End: fmtDateTime(u.end_date, u.end_time),
                Questions: u.query_count ?? "",
                Correct: u.correct_count ?? "",
                Incorrect: Math.max(0, (Number(u.query_count ?? 0) - Number(u.correct_count ?? 0))),
                Skills: [
                    u.topic1_name,
                    u.topic2_name,
                    u.topic3_name,
                    u.topic4_name,
                    u.topic5_name,
                ]
                    .filter(Boolean)
                    .join("; "),
                "Recommended Courses": courseTitles,
            };
        });

        const courseDetails = users.flatMap((u, idx) => {
            const courses = coursesByIdx[idx] || [];
            if (!courses.length) return [];
            return courses.map((c, i) => ({
                "#": i + 1,
                "User Name": u.user_name || "",
                "User Email": u.user_email || "",
                "Course Title": c.title || "",
                Provider: c.provider || "",
                URL: c.url || "",
            }));
        });

        const stamp = new Date().toISOString().slice(0, 10);
        const baseName = `HPE Digital Skill Advisor - Manager created assessment - ${headerMeta?.name} - ${stamp}`;

        try {
            const XLSX = await import("xlsx");
            const wb = XLSX.utils.book_new();
            const ws = XLSX.utils.json_to_sheet(rows);
            XLSX.utils.book_append_sheet(wb, ws, "Users");

            if (headerMeta?.skills?.length) {
                const skillsRows = headerMeta.skills.map((s, idx) => ({ "#": idx + 1, Skill: s }));
                const ws2 = XLSX.utils.json_to_sheet(skillsRows);
                XLSX.utils.book_append_sheet(wb, ws2, "Assessment Skills");
            }

            if (courseDetails.length) {
                const ws3 = XLSX.utils.json_to_sheet(courseDetails);
                XLSX.utils.book_append_sheet(wb, ws3, "Course Details");
            }

            XLSX.writeFile(wb, `${baseName}.xlsx`);
            setToast("Export complete.");
        } catch (e) {
            // CSV fallback (only Users sheet)
            const headers = Object.keys(rows[0] || {});
            const esc = (v) => {
                const s = (v ?? "").toString();
                return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
            };
            const lines = [headers.map(esc).join(","), ...rows.map((r) => headers.map((h) => esc(r[h])).join(","))];
            const csv = lines.join("\n");

            const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `${baseName}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);

            setToast("Export complete (CSV).");
        }
    };
    // -----------------------------------------------------------

    return (
        <Box height="100vh" direction="column" background="light-2">
            <HeaderNavigation />

            <Box
                as="main"
                flex={{ grow: 1, shrink: 1 }}
                overflow={{ vertical: "auto" }}
                pad={{ horizontal: "medium", top: "medium", bottom: "xlarge" }}
                style={{ minHeight: 0, minWidth: 0 }}
                gap="medium"
            >
                <Box direction="row" align="center" justify="between">
                    <Heading level={2} margin="none" padding="small">
                        Recommendations by User
                    </Heading>
                    <Button label="Export assessment data" onClick={handleExport} secondary disabled={!group || users.length === 0} />
                </Box>

                {loading ? (
                    <Box fill align="center" justify="center" pad="large" gap="small">
                        <Spinner size="large" />
                        <Text>Loading…</Text>
                    </Box>
                ) : error ? (
                    <Text color="status-critical">{error}</Text>
                ) : !group ? (
                    <Text>No data available.</Text>
                ) : (
                    <>
                        {/* Assessment summary + skills */}
                        <Box background="white" round="small" elevation="small" pad="medium" gap="small">
                            <Heading level={3} margin="none">
                                {headerMeta.name}
                            </Heading>

                            <Box direction="row" gap="medium" justify="between" align="start">
                                {/* LEFT: assessment details */}
                                <Box flex={{ grow: 1 }}>
                                    <Text>
                                        <strong>Created:</strong> {fmtDate(headerMeta.createdDate)}
                                        {headerMeta.createdTime ? ` ${headerMeta.createdTime}` : ""}
                                    </Text>
                                    <Text>
                                        <strong>Manager:</strong> {headerMeta.managerName}
                                        {headerMeta.managerEmail ? ` · ${headerMeta.managerEmail}` : ""} · {headerMeta.managerCountry}
                                    </Text>
                                    <Text margin={{ bottom: "small" }}>
                                        <strong>Users:</strong> {headerMeta.usersCount} &nbsp;|&nbsp; <strong>Completed:</strong>{" "}
                                        {headerMeta.completed}
                                    </Text>
                                </Box>

                                {/* RIGHT: skills list */}
                                <Box flex={{ shrink: 0 }} style={{ maxWidth: "45%" }}>
                                    <Text weight="bold">Skills in this assessment:</Text>
                                    {headerMeta.skills?.length ? (
                                        <Box
                                            pad={{ left: "small", top: "xsmall" }}
                                            gap="xxsmall"
                                            overflow={{ vertical: "auto" }}
                                            style={{ maxHeight: "150px" }}
                                        >
                                            {headerMeta.skills.map((s, i) => (
                                                <Text key={i}>• {s}</Text>
                                            ))}
                                        </Box>
                                    ) : (
                                        <Text color="dark-5">No skills found.</Text>
                                    )}
                                </Box>
                            </Box>
                        </Box>


                        {/* Users list */}
                        <Box gap="small">
                            <Heading level="3" margin={{ vertical: "small" }}>
                                Users
                            </Heading>

                            {users.map((u) => {
                                const canView = u.is_complete === 1;
                                const hasCourses = (coursesByUser[u.user_id] || []).length > 0;

                                return (
                                    <Box
                                        key={String(u.user_id)}
                                        background="white"
                                        round="small"
                                        elevation="xsmall"
                                        pad="medium"
                                    >
                                        {/* Row: details (left) → courses (middle) → button (right) */}
                                        <Box direction="row" align="start" justify="between" gap="medium" wrap>
                                            {/* Details */}
                                            <Box gap="xxsmall" flex={{ grow: 1, shrink: 0 }}>
                                                <Text weight="bold">
                                                    {u.user_name || u.user_email || "Unnamed user"}
                                                </Text>
                                                <Text size="small" color="dark-5" margin={{ bottom: "small" }}>
                                                    {u.user_email || "—"} · {canView ? "Completed" : "Not completed"} ·{" "}
                                                    Ended: {fmtDate(u.end_date)}
                                                </Text>
                                            </Box>

                                            {/* Recommended courses */}
                                            <Box direction="row" gap="small">
                                                <Button
                                                    label="Show recommended courses"
                                                    secondary
                                                    disabled={!canView}
                                                    onClick={() => openCoursesModal(u)}
                                                    tip={!canView ? "Available after completion" : hasCourses ? undefined : "Loaded on open"}
                                                />

                                                <Button
                                                    label="View recommendations"
                                                    secondary
                                                    disabled={!canView}
                                                    onClick={() => setRecUser(u)}
                                                    tip={!canView ? "Available after completion" : undefined}
                                                />
                                            </Box>
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Box>

                    </>
                )}
            </Box>

            {/* Recommendations Modal (AssessmentBlock inside) */}
            {recUser && (
                <Layer onEsc={() => setRecUser(null)} onClickOutside={() => setRecUser(null)} responsive={false} modal>
                    <Box pad="medium" width="xlarge" height={{ max: "85vh" }} overflow={{ vertical: "auto" }} gap="small">
                        <Box direction="row" align="center" justify="between">
                            <Heading level={3} margin="none">
                                Recommendations — {recUser.user_name || recUser.user_email}
                            </Heading>
                            <Button label="Close" onClick={() => setRecUser(null)} />
                        </Box>
                        <AssessmentBlock user_id={recUser.user_id} assessment_id={String(group.assessment_id)} />
                    </Box>
                </Layer>
            )}

            {coursesUser && (
                <Layer onEsc={() => setCoursesUser(null)} onClickOutside={() => setCoursesUser(null)} responsive={false} modal>
                    <Box pad="medium" width="large" height={{ max: "75vh" }} overflow={{ vertical: "auto" }} gap="small">
                        <Box direction="row" align="center" justify="between">
                            <Heading level={3} margin="none">
                                Recommended Courses — {coursesUser.user_name || coursesUser.user_email}
                            </Heading>
                            <Button label="Close" onClick={() => setCoursesUser(null)} />
                        </Box>

                        {coursesLoading ? (
                            <Box align="center" justify="center" pad="medium" gap="small">
                                <Spinner />
                                <Text>Loading courses…</Text>
                            </Box>
                        ) : (
                            <Box gap="xsmall">
                                {(() => {
                                    const list = coursesByUser[coursesUser.user_id] || [];
                                    if (!list.length) {
                                        return <Text color="text-weak">No courses available for this user.</Text>;
                                    }
                                    return list.map((c, i) => (
                                        <Box key={`${c.id || i}`} pad="xsmall">
                                            {c.url ? (
                                                <Anchor href={c.url} target="_blank" rel="noopener">
                                                    {c.title || "Course"}
                                                </Anchor>
                                            ) : (
                                                <Text>{c.title || "Course"}</Text>
                                            )}
                                            {c.provider ? (
                                                <Text size="small" color="dark-5">
                                                    &nbsp;• {c.provider}
                                                </Text>
                                            ) : null}
                                        </Box>
                                    ));
                                })()}
                            </Box>
                        )}
                    </Box>
                </Layer>
            )}


            {toast && (
                <Notification toast status="normal" title="Success" message={toast} onClose={() => setToast("")} />
            )}

            <StickyFooter />
        </Box>
    );
};

export default ManagerAssessmentRecommendationsPage;
